import {IBrowser, IService, SystemComponentType} from '@gongxigames/framework/core/types';
import {injectable} from '@gongxigames/framework/core/installer';
import {CoreDI} from '@gongxigames/framework/core/configs';

@injectable()
export class Browser implements IBrowser, IService {
    public static NAME: symbol = CoreDI.Browser;
    public type: SystemComponentType = 'service';

    private _window: Window;
    private _lastTouchEnd = 0;
    private _timeoutsIDs: number[] = [];
    private _intervalsIDs: number[] = [];
    private _touchEndListener: (e: any) => void = this.onTouchEnd.bind(this);
    private listenersMap: Record<string, EventListener[]> = {};

    constructor() {
        this._window = window;
    }

    public gotoURL(url: string, openOnParent?: boolean): void {
        if (openOnParent && this._window.top) {
            this._window.top.location.href = url;
        } else {
            this._window.location.href = url;
        }
    }

    public gotoBack(): void {
        this._window.history.go(-1);
    }

    public openNewTab(url: string): void {
        this._window.open(url, '_blank')?.focus();
    }

    public reloadPage(): void {
        this._window.location.reload();
    }

    public postMessage(message: any, targetOrigin = '*'): void {
        this._window.postMessage(message, targetOrigin);
    }

    public postMessageToParent(message: any, targetOrigin = '*', transfer?: Transferable[]): void {
        if (this._window.parent) {
            this._window.parent.postMessage(message, targetOrigin, transfer);
        }
    }

    public postMessageToTop(message: any, targetOrigin = '*'): void {
        if (this._window.top) {
            this._window.top.postMessage(message, targetOrigin);
        }
    }

    public createMessageChanel(): MessageChannel {
        return new MessageChannel();
    }

    public setOnBeforeUnloadedCallback(callback: Function): void {
        this._window.onbeforeunload = callback as any;
    }

    public disableDoubleTapToZoom(): void {
        this._lastTouchEnd = 0;

        this.addEventListener('touchend', this._touchEndListener, false);
    }

    private onTouchEnd(e: TouchEvent): void {
        const now = (new Date()).getTime();

        if (now - this._lastTouchEnd <= 300) {
            e.preventDefault();
        }
        this._lastTouchEnd = now;
    }

    public addEventListener(type: string, listener: (event: Event) => void, options?: any): void {

        if (!this.listenersMap[type]) {
            this.listenersMap[type] = [];
        }

        this.listenersMap[type].push(listener);

        this._window.addEventListener(type, listener, options);
    }

    public removeEventListener(type: string, listener: (event: Event) => void, options?: any): void {
        if (this.listenersMap[type]) {
            this.listenersMap[type] = this.listenersMap[type].filter(l => l !== listener);
        }

        this._window.removeEventListener(type, listener, options);
    }

    public removeAllListeners(): void {
        for (const type in this.listenersMap) {
            if (!this.listenersMap.hasOwnProperty(type) || !this.listenersMap[type]) {
                continue;
            }

            const listeners = this.listenersMap[type];

            listeners.forEach(listener => {
                this._window.removeEventListener(type, listener);
            });
        }
    }

    public addTimeout(handler: () => void, timeout = 0): number {
        const timeoutID = this._window.setTimeout(() => {
            handler();

            this._timeoutsIDs = this._timeoutsIDs.filter(id => id !== timeoutID);
        }, timeout);

        this._timeoutsIDs.push(timeoutID);

        return timeoutID;
    }

    public addInterval(handler: () => void, timeout = 0): number {
        const intervalID = this._window.setInterval(handler, timeout);

        this._intervalsIDs.push(intervalID);

        return intervalID;
    }

    public removeTimeout(timeoutID: number): void {
        this._window.clearTimeout(timeoutID);
        this._timeoutsIDs = this._timeoutsIDs.filter(id => id !== timeoutID);
    }

    public removeInterval(intervalID: number): void {
        this._window.clearInterval(intervalID);
        this._intervalsIDs = this._intervalsIDs.filter(id => id !== intervalID);
    }

    public removeAllTimeouts(): void {
        this._timeoutsIDs.forEach(id => this._window.clearTimeout(id));
        this._intervalsIDs.forEach(id => this._window.clearInterval(id));
        this._timeoutsIDs = [];
        this._intervalsIDs = [];
    }

    public destroy(): void {
        this.removeAllListeners();
        this.removeAllTimeouts();
    }
}
